import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {NavigationType, useLocation, useNavigationType} from "react-router-dom";
import {recallScroll, rememberScroll} from "../../../navigation/scrollMemory";
import {buildTocTree, type TocEntry} from "../tocUtils";
import {findAnchorTarget} from "../anchorTarget";
import type {DibDocument} from "../types";

const collectTocIds = (entries: TocEntry[]): string[] =>
    entries.flatMap((e) => [e.id, ...collectTocIds(e.children)]);

/**
 * Owns everything that keeps the TOC pane and the article in sync for the
 * legacy HTML resource view: the active TOC entry (highlight + expansion),
 * scroll-spy while the user scrolls the article, the article jump to the
 * URL hash on document load (or, when returning to a history entry, the
 * restore of its recorded reading position), the initial TOC-pane scroll to
 * the active entry, and TOC-click navigation. The consumer attaches the returned refs
 * to the article and TOC nav elements and forwards activeId/handleTocClick.
 * Automatic tracking is suppressed during programmatic scrolls and stays
 * suppressed until the user's first interaction afterwards, so a clicked or
 * deep-linked entry keeps the highlight no matter how long the scroll runs.
 */
export const useTocScrollSync = (
    doc: DibDocument | undefined,
    resourceId: string,
    segmentId: string | undefined
) => {
    const articleRef = useRef<HTMLElement>(null);
    const tocNavRef = useRef<HTMLElement>(null);
    const isTrackingSuppressed = useRef(false);
    const releaseOnNextScroll = useRef(false);
    const pendingTocScrollId = useRef<string | null>(null);
    const [activeId, setActiveId] = useState<string | null>(
        () => window.location.hash.slice(1) || null
    );
    const location = useLocation();
    const navigationType = useNavigationType();

    // Record the article's scroll position per history entry so a return to
    // the entry — back/forward navigation or a reload — can restore the
    // reading position (see the document-load effect below).
    useEffect(() => {
        const root = articleRef.current;
        if (!root) return;
        const save = () =>
            rememberScroll(location.key, location.pathname, root.scrollTop);
        root.addEventListener("scroll", save, {passive: true});
        return () => root.removeEventListener("scroll", save);
    }, [location.key, location.pathname]);

    // Automatic highlight tracking (scroll-spy + top seeding) is suppressed
    // from the moment a programmatic scroll starts (TOC click, hash jump)
    // until the first genuine user interaction afterwards — releasing on a
    // timer instead would let a long smooth scroll cascade the highlight
    // through every section it passes, and the band winner at rest is the
    // clicked entry's first child, not the clicked entry. Both helpers clear
    // the pending scroll-release so a stale arm can't leak across
    // suppressions.
    const suppressTracking = useCallback(() => {
        isTrackingSuppressed.current = true;
        releaseOnNextScroll.current = false;
    }, []);
    const resumeTracking = useCallback(() => {
        isTrackingSuppressed.current = false;
        releaseOnNextScroll.current = false;
    }, []);

    // Mark the segment being navigated to as active as soon as the URL changes,
    // so the kept-visible TOC highlights the incoming segment during the load.
    // The article isn't rendered yet (no sections for the scroll-spy), and a
    // seg=1 entry is a <Link> that never calls handleTocClick. Scroll-spy
    // refines this once the segment renders.
    useEffect(() => {
        if (segmentId) setActiveId(segmentId);
    }, [segmentId]);

    // Set of section IDs that appear in the TOC. The IntersectionObserver
    // observes only these so non-TOC sections in the article body can't
    // hijack the active highlight.
    const tocIds = useMemo(() => {
        if (!doc) return new Set<string>();
        return new Set(collectTocIds(buildTocTree(doc)));
    }, [doc]);

    // On each document load, resolve which TOC entry the TOC pane should
    // scroll to: the URL hash when it maps to a TOC entry, else the segment.
    // activeId must be settled on that target first — TOC entries only exist
    // in the DOM once their ancestor chain is expanded, and expansion derives
    // from activeId. The actual scroll runs in the effect below once the
    // active entry is committed.
    useEffect(() => {
        if (!doc || tocIds.size === 0) return;
        const hash = window.location.hash.slice(1);
        const target =
            hash && tocIds.has(hash)
                ? hash
                : segmentId && tocIds.has(segmentId)
                  ? segmentId
                  : null;
        if (!target) return;
        setActiveId(target);
        pendingTocScrollId.current = target;
    }, [doc, tocIds, segmentId]);

    // Consume the pending TOC scroll once the target is the rendered active
    // entry: same effect pass when setActiveId bailed (already active), or
    // the follow-up commit when it re-rendered — `doc` is deliberately a dep
    // for the former even though nothing here reads it. Entries already fully
    // inside the nav viewport are left alone so short TOCs, top-of-document
    // loads, and TOC-click navigations don't jump.
    useEffect(() => {
        const target = pendingTocScrollId.current;
        if (!target || activeId !== target) return;
        pendingTocScrollId.current = null;
        const nav = tocNavRef.current;
        const link = nav?.querySelector<HTMLAnchorElement>(
            'a[aria-current="location"]'
        );
        if (!nav || !link) return;
        const navRect = nav.getBoundingClientRect();
        const linkRect = link.getBoundingClientRect();
        if (linkRect.top >= navRect.top && linkRect.bottom <= navRect.bottom)
            return;
        link.scrollIntoView({block: "center"});
    }, [activeId, doc]);

    // Resume automatic highlight tracking on the first genuine user
    // interaction with the article after a programmatic scroll. Two channels:
    // direct input events (a programmatic scroll produces none of these, so
    // they are unambiguous user signals — capture phase so a stopPropagation
    // inside rendered content can't swallow them), and, for input the DOM
    // cannot observe directly (scrollbar drags in Firefox, find-in-page), the
    // first scroll event after the programmatic scroll has come to rest:
    // scrollend arms it, so the programmatic scroll's own events can never
    // release. Input listeners are article-scoped on purpose — a pointerdown
    // on the TOC pane must NOT release, or a second TOC click mid-flight
    // would let a spy callback flash a wrong highlight in the gap between
    // pointerdown and handleTocClick re-suppressing. scroll/scrollend don't
    // bubble, so the plain listeners see only the article's own scrolling.
    // Attached once: the consumer renders the article element unconditionally.
    useEffect(() => {
        const root = articleRef.current;
        if (!root) return;
        const arm = () => {
            if (isTrackingSuppressed.current)
                releaseOnNextScroll.current = true;
        };
        const releaseIfArmed = () => {
            if (releaseOnNextScroll.current) resumeTracking();
        };
        const capture = {capture: true, passive: true} as const;
        root.addEventListener("wheel", resumeTracking, capture);
        root.addEventListener("touchstart", resumeTracking, capture);
        root.addEventListener("pointerdown", resumeTracking, capture);
        root.addEventListener("keydown", resumeTracking, capture);
        root.addEventListener("scrollend", arm);
        root.addEventListener("scroll", releaseIfArmed, {passive: true});
        return () => {
            root.removeEventListener("wheel", resumeTracking, capture);
            root.removeEventListener("touchstart", resumeTracking, capture);
            root.removeEventListener("pointerdown", resumeTracking, capture);
            root.removeEventListener("keydown", resumeTracking, capture);
            root.removeEventListener("scrollend", arm);
            root.removeEventListener("scroll", releaseIfArmed);
        };
    }, [resumeTracking]);

    // On document (re)load, scroll to the URL hash if present. Suppression is
    // taken synchronously, not inside the rAF: the scroll-spy observer created
    // below delivers its initial callback as a browser task that can beat the
    // rAF, and unsuppressed it would read the pre-jump state (article still at
    // top) and clobber the hash-derived highlight with the topmost section.
    // The reset on entry releases a suppression left over from the previous
    // document — suppressions no longer time out, they end on user interaction
    // (listeners above) — and the re-assert inside the rAF clears an arm set
    // by a scrollend that slipped in between. Released right away when the
    // hash target doesn't exist, since nothing scrolled.
    useEffect(() => {
        resumeTracking();
        if (!doc?.access || doc.root.length === 0) return;
        // Returning to this history entry (back/forward, or a reload — both
        // are POPs): restore the recorded reading position instead of jumping
        // to the hash. Tracking is deliberately left running so the scroll-spy
        // recomputes the highlight from wherever the restore lands.
        const restoredTop =
            navigationType === NavigationType.Pop
                ? recallScroll(location.key, location.pathname)
                : undefined;
        if (restoredTop !== undefined) {
            requestAnimationFrame(() => {
                const root = articleRef.current;
                if (root) root.scrollTop = restoredTop;
            });
            return;
        }
        const hash = window.location.hash.slice(1);
        if (!hash) return;
        suppressTracking();
        requestAnimationFrame(() => {
            // Exact id first; if none, a unique case-insensitive match inside
            // the article (related-source anchors arrive as "P3-5" for "p3-5").
            const el = findAnchorTarget(hash, document, articleRef.current);
            if (!el) {
                resumeTracking();
                return;
            }
            suppressTracking();
            el.scrollIntoView({block: "start"});
        });
    }, [
        doc,
        resourceId,
        segmentId,
        navigationType,
        location.key,
        location.pathname,
        suppressTracking,
        resumeTracking,
    ]);

    // Seed activeId to the topmost rendered TOC section whenever the
    // article is at the top. The IntersectionObserver's -10%/-70% band can
    // miss short top-of-document sections, leaving the TOC un-highlighted at
    // scrollTop 0 — both on initial load and after the user scrolls back to
    // the top from anywhere in the document. The seed is the first
    // `section[id]` in the article DOM that maps to a TOC entry, NOT the
    // first entry of the whole TOC tree: in a segmented topic the article
    // only renders the current segment's sections, so the tree's first id
    // need not be present here. A scroll listener subsumes the old
    // initial-load-only seeding effect: we run the check once on mount to
    // cover the no-hash open-at-top case (a scroll event doesn't fire just
    // because the listener attaches), and the listener handles every
    // subsequent return-to-top. The initial-mount check is skipped when
    // window.location.hash is set so the hash-driven scroll/highlight wins;
    // after the hash scroll fires, scrollTop is non-zero so the listener
    // can't clobber it. Tracking suppression is respected so a TOC click
    // that scrolls to the first entry doesn't double-fire.
    useEffect(() => {
        const root = articleRef.current;
        if (!root || tocIds.size === 0) return;

        const seedIfAtTop = () => {
            if (isTrackingSuppressed.current) return;
            if (root.scrollTop !== 0) return;
            const topSection = Array.from(
                root.querySelectorAll<HTMLElement>("section[id]")
            ).find((s) => tocIds.has(s.id));
            if (!topSection) return;
            setActiveId((prev) =>
                prev === topSection.id ? prev : topSection.id
            );
        };

        if (!window.location.hash) seedIfAtTop();

        root.addEventListener("scroll", seedIfAtTop, {passive: true});
        return () => root.removeEventListener("scroll", seedIfAtTop);
    }, [tocIds]);

    // IntersectionObserver scroll-spy: the topmost currently-intersecting
    // TOC section becomes activeId — computed from a persistent set of
    // in-band sections (observer entries only report changes), with
    // wrapping ancestors pruned so nested sections resolve to the innermost
    // entry. Suppressed from a programmatic scroll until the user interacts
    // again, though the set bookkeeping continues so the picture is current
    // when tracking resumes.
    useEffect(() => {
        const root = articleRef.current;
        if (!root || !doc) return;

        const sections = Array.from(
            root.querySelectorAll<HTMLElement>("section[id]")
        ).filter((s) => tocIds.has(s.id));

        const inBand = new Set<HTMLElement>();

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        inBand.add(entry.target as HTMLElement);
                    } else {
                        inBand.delete(entry.target as HTMLElement);
                    }
                }
                if (isTrackingSuppressed.current) return;
                // Sections nest, so a wrapping ancestor is in-band whenever
                // a descendant is and would always win a topmost sort — keep
                // only innermost members. Rects are re-measured here: entry
                // rects are stale for members unchanged in this callback.
                const members = Array.from(inBand);
                const topMost = members
                    .filter(
                        (s) =>
                            !members.some(
                                (other) => other !== s && s.contains(other)
                            )
                    )
                    .sort(
                        (a, b) =>
                            a.getBoundingClientRect().top -
                            b.getBoundingClientRect().top
                    )[0];
                if (topMost) setActiveId(topMost.id);
            },
            {root, rootMargin: "-10% 0px -70% 0px", threshold: 0}
        );

        sections.forEach((s) => observer.observe(s));
        return () => observer.disconnect();
    }, [doc, tocIds]);

    const handleTocClick = (id: string) => {
        suppressTracking();
        setActiveId(id);
        // Keep the existing state object: React Router stores {idx, key}
        // there, and both back navigation and scroll memory depend on it.
        window.history.replaceState(window.history.state, "", `#${id}`);
        document
            .getElementById(id)
            ?.scrollIntoView({behavior: "smooth", block: "start"});
    };

    return {articleRef, tocNavRef, activeId, handleTocClick};
};
